import React, { useMemo, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import styles from './multiSelectDropdown.module.scss';
import { Checkbox, Icon } from '../..';
import { useClickOutside } from '../../../utils';

const castValue = (value, type) => {
  if (type === 'number') return Number(value);
  if (type === 'boolean') return value === true || value === 'true';
  return value;
};

const MultiSelectDropdown = ({
  array = [],
  name = 'name',
  value = 'value',
  valueType,
  state = [],
  setState,
  placeholder,
  allText,
  searchPlaceholder,
  disabled,
}) => {
  const dropdownRef = useRef(null);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useClickOutside(dropdownRef);

  const selectedSet = useMemo(() => new Set(state), [state]);

  const normalizedArray = useMemo(
    () =>
      array.map(item => ({
        ...item,
        _value: castValue(item[value], valueType),
      })),
    [array, value, valueType]
  );

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();

    return normalizedArray.filter(item =>
      String(item[name])
        .toLowerCase()
        .includes(q)
    );
  }, [normalizedArray, name, query]);

  const allValues = useMemo(() => normalizedArray.map(item => item._value), [
    normalizedArray,
  ]);

  const isAllChecked =
    normalizedArray.length > 0 && state.length === normalizedArray.length;

  const handleItemChange = ({ target: { value: rawValue, checked } }) => {
    // Ignore clicks on "Visi" checkbox - it's handled by handleCheckAll
    if (rawValue === '-1') {
      return;
    }
    const nextValue = castValue(rawValue, valueType);
    let nextState = state.slice();
    if (checked) {
      if (!nextState.includes(nextValue)) {
        nextState = [...nextState, nextValue];
      }
    } else {
      nextState = nextState.filter(item => item !== nextValue);
    }
    setState(nextState);
  };

  const handleCheckAll = ({ target: { checked } }) => {
    setState(checked ? allValues : []);
  };

  const handleToggle = () => {
    if (!disabled) setIsOpen(!isOpen);
  };

  const displayValue = useMemo(() => {
    if (!state.length) return placeholder;
    if (state.length === allValues.length) return allText;
    return `${state.length} pasirinkimai`;
  }, [state.length, allValues.length, placeholder, allText]);

  useEffect(() => {
    if (!isOpen) setQuery('');
  }, [isOpen]);

  return (
    <div className={styles.container} ref={dropdownRef}>
      <button
        type="button"
        className={cn(styles.control, disabled && styles.disabled)}
        onClick={handleToggle}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span
          className={cn(styles.value, !state.length && styles.placeholderValue)}
        >
          {displayValue}
        </span>
        <Icon name="chevron-down" classes={{ icon: styles.icon }} />
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.search}>
            <input
              type="search"
              placeholder={searchPlaceholder}
              className={styles.input}
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            <Icon name="search" classes={{ icon: styles.searchIcon }} />
          </div>

          <div className={styles.list}>
            {!query && (
              <Checkbox
                label={allText}
                name={allText}
                value="-1"
                checked={isAllChecked}
                onChange={handleCheckAll}
              />
            )}

            {filteredItems.map(item => (
              <Checkbox
                key={item[value]}
                label={item[name]}
                name={item[name]}
                value={item[value]}
                checked={selectedSet.has(item._value)}
                onChange={handleItemChange}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

MultiSelectDropdown.defaultProps = {
  array: [],
  name: 'name',
  value: 'value',
  placeholder: 'Pasirinkti',
  allText: 'Visi',
  searchPlaceholder: 'Paieška',
  disabled: false,
  state: [],
};

MultiSelectDropdown.propTypes = {
  array: PropTypes.arrayOf(PropTypes.object),
  name: PropTypes.string,
  value: PropTypes.string,
  valueType: PropTypes.oneOf(['string', 'number', 'boolean']).isRequired,
  state: PropTypes.array,
  setState: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  allText: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  disabled: PropTypes.bool,
};

export default MultiSelectDropdown;
